/****************************************************
 * Module Name:  dayWiseStoneSales.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Tooltip, Typography, TextField, Button, AppBar, Tabs, Tab } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import InvoiceReport from "./invoiceReport";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import SwipeableViews from "react-swipeable-views";
import CancelledStoneReport from "./cancelledStoneReport";
import PageTitle from "../common/pageTitle";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

const DayWiseStoneSales = () => {

    const theme = useTheme();
    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    const dayWiseStoneSalesPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.REPORT_DAY_WISE_STONE_SALES);

    const [stockData, setStockData] = useState([]);
    const [loader, setLoader] = useState();
    const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
    const [toDate, setToDate] = useState(dayjs());
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const defaultStatus = [{ value: -1, label: "-- ANY --" }];
    const [invoiceDate, setInvoiceDate] = useState();

    const [totalSell, setTotalSell] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalCancelledStone, setTotalCancelledStone] = useState(0);
    const [totalCancelledAmount, setTotalCancelledAmount] = useState(0);
    const [effectiveAmount, setEffectiveAmount] = useState(0);

    const columns = [
        {
            headerName: "DATE",
            field: "date",
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            width: 180,
            renderCell: (params) => dayjs(params.value).format("DD/MM/YYYY"),
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "DISTINCT CUSTOMER",
            field: "distinctcustomer",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 290,
            renderCell: renderCellExpand
        },
        {
            headerName: "SELL COUNT",
            field: "sellcount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 260,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => {
                            setInvoiceDate(params.row.date)
                            setShowInvoiceModal(true)
                            setValue(0)
                        }}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
        {
            headerName: "TOTAL AMOUNT",
            field: "totalamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 250,
            renderCell: (params) => (params.value).toFixed(2)
        },
        {
            headerName: "CANCELLED STONE COUNT",
            field: "cancelledstonecount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => {
                            setInvoiceDate(params.row.date)
                            setShowInvoiceModal(true)
                            setValue(1)
                        }}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
        {
            headerName: "CANCELLED STONE AMOUNT",
            field: "cancelledstoneamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => !params.value ? params.value : (params.value).toFixed(2)
        },
        {
            headerName: "EFFECTIVE SELL",
            field: "effectiveamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => (params.row.totalamount - params.row.cancelledstoneamount).toFixed(2)
        },
    ];

    useEffect(() => {
        PageTitle("REPORT_DAY_WISE_STONE_SALES");
        if (Utility.isReadAllowed(dayWiseStoneSalesPermission)) {
            handleSearch();
        }
    }, []);

    const handleSearch = () => {
        let data = {
            from: dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z",
            to: dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z"
        }

        setLoader(true);
        ReportProxy.GetDayWiseStoneSales(data, (response) => {
            if (response) {

                let tempResponse = response.sort((a, b) => a.date.localeCompare(b.date));
                setStockData(tempResponse);
                getSummaryReport(tempResponse);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const getSummaryReport = (response) => {
        let totalSell = 0, totalAmount = 0, totalCancelledStone = 0,
            totalCancelledAmount = 0, effectiveAmount = 0;
        response.forEach(iData => {
            totalSell += iData.sellcount;
            totalAmount += iData.totalamount;
            totalCancelledStone += iData.cancelledstonecount;
            totalCancelledAmount += iData.cancelledstoneamount;
            effectiveAmount += (iData.totalamount - iData.cancelledstoneamount);
        });

        setTotalSell(totalSell);
        setTotalAmount(totalAmount);
        setTotalCancelledStone(totalCancelledStone);
        setTotalCancelledAmount(totalCancelledAmount);
        setEffectiveAmount(effectiveAmount);
    }

    return (
        <Box>
            {!Utility.isReadAllowed(dayWiseStoneSalesPermission) ? <NoPermission /> : (
                <Box sx={{ mt: "8px" }}>

                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                        handleClose={() => setShowSnackbar(false)}
                    />

                    {/* Date Filter */}
                    <Box display={"flex"} gap={"10px"}>
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap="10px"
                            width={"11%"}
                            sx={{
                                "& .MuiOutlinedInput-input": {
                                    height: "13px",
                                }
                            }}
                        >
                            <Typography>From</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    ampm={true}
                                    renderInput={(props) => (
                                        <TextField
                                            fullWidth
                                            {...props}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    value={fromDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        // Get the current time
                                        const now = dayjs();

                                        // Set startOfMonth to the first day of the selected month, but with the current system time
                                        const date = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();

                                        setFromDate(date);
                                    }}
                                    sx={{
                                        width: "60%"
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap="10px"
                            width={"10%"}
                            sx={{
                                "& .MuiOutlinedInput-input": {
                                    height: "13px",
                                }
                            }}
                        >
                            <Typography>To</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    ampm={true}
                                    renderInput={(props) => (
                                        <TextField
                                            fullWidth
                                            {...props}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    value={toDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        // Get the current time
                                        const now = dayjs();

                                        // Set startOfMonth to the first day of the selected month, but with the current system time
                                        const date = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();

                                        setToDate(date);
                                    }}
                                    sx={{
                                        width: "60%"
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <LoadingButton
                            variant="contained"
                            onClick={() => handleSearch()}
                            type="Submit"
                            loading={loader}
                            loadingPosition="start"
                            startIcon={<SearchOutlinedIcon />}
                        >
                            Search
                        </LoadingButton>
                    </Box>

                    {/* Table */}
                    <Box
                        sx={{
                            marginTop: "8px",
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '78vh'
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: GridToolbar }}
                            rows={stockData}
                            columns={columns}
                            getRowId={(row) => row.date}
                            loading={loader}
                            onRowDoubleClick={(params) => {
                                setInvoiceDate(params.row.date)
                                setShowInvoiceModal(true)
                                setValue(0);
                            }}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        stoneid: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />

                        <Box
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: "288px",
                                width: "55%",
                                display: 'flex',
                                justifyContent: "center",
                                alignItems: 'center',
                                color: "black",
                                fontWeight: 'normal',
                                fontSize: Constants.defaultTableFontSize
                            }}
                        ><p style={{ margin: 0 }}>
                                <span>Total Sell: </span> <b>{totalSell}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Amount: </span> <b>{totalAmount.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Cancelled Stone: </span> <b>{totalCancelledStone}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Cancelled Amount: </span> <b>{totalCancelledAmount.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Effective Sell: </span> <b>{effectiveAmount.toFixed(2)}</b></p>
                        </Box>

                        <Box style={{ position: "absolute", top: 7, right: "265px", display: "flex", alignItems: "center" }}>
                            {loader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>

                    </Box>

                    {showInvoiceModal ? (
                        <Modal
                            enforceFocus={false}
                            show={true}
                            onHide={() => setShowInvoiceModal(false)}
                            backdrop="static"
                            dialogClassName="max-modalSize-Tab"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <img
                                        src={logo}
                                        height="40pt"
                                        width="40pt"
                                        alt={Constants.COMPANY_SITE}
                                    />
                                    &nbsp;&nbsp;
                                    Invoice Details
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {value === 2 ? <NoPermission /> :
                                    <Box sx={{
                                        bgcolor: "background.paper",
                                        width: "100%",
                                        padding: "10px",
                                        position: "relative"
                                    }}>
                                        <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
                                            <Tabs
                                                value={value}
                                                onChange={handleChange}
                                                indicatorColor="secondary"
                                                textColor="inherit"
                                                variant="fullWidth"
                                                aria-label="full width tabs example"
                                                sx={{
                                                    "& .MuiButtonBase-root .MuiTab-root": {
                                                        color: "#212121",
                                                        fontWeight: "bold",
                                                        textAlign: 'center',
                                                        minHeight: "50px",
                                                    },
                                                    "& .MuiTab-root": {
                                                        paddingBottom: "12px"
                                                    },
                                                    "& .Mui-selected": {
                                                        bgcolor: "#0277bd !important",
                                                        color: "#fafafa !important",
                                                        fontWeight: "bold !important",
                                                        minHeight: "50px",
                                                    }
                                                }}
                                                TabIndicatorProps={{ style: { background: "#0277bd" } }}
                                            >
                                                {/* Invoice Details */}
                                                <Tab
                                                    // icon={<TbCalendarDollar size={18} />}
                                                    iconPosition="start"
                                                    label="Invoice Details"
                                                    {...a11yProps(0)}
                                                />

                                                {/* Cancelled Stones */}
                                                <Tab
                                                    // icon={<UploadFileIcon size={18} />}
                                                    iconPosition="start"
                                                    label="Cancelled Stones"
                                                    {...a11yProps(1)}
                                                />
                                            </Tabs>
                                        </AppBar>
                                        <SwipeableViews
                                            axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                                            index={value}
                                            onChangeIndex={handleChangeIndex}
                                        >
                                            {/* Invoice Details */}
                                            <TabPanel value={value} index={0}>
                                                <InvoiceReport
                                                    defaultfromdate={invoiceDate}
                                                    defaulttodate={invoiceDate}
                                                    defaultStatus={defaultStatus}
                                                    fromOtherComponent={true}
                                                    heightFromOtherComponent={"58vh"}
                                                />
                                            </TabPanel>

                                            {/* Cancelled Stones */}
                                            <TabPanel value={value} index={1}>
                                                <CancelledStoneReport invoiceDate={invoiceDate} />
                                            </TabPanel>
                                        </SwipeableViews>

                                    </Box>}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="contained" color="error" onClick={() => setShowInvoiceModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>
                    ) : null}

                </Box>
            )}
        </Box>
    );
};

export default DayWiseStoneSales;